import request from '@/Utilities/httpRequest';
import utilities from '@/Utilities/common';
import operatingSystem from '@/Utilities/operating-system';
import apiCombos from '@/api/combos';
import websiteEvents from '@/Utilities/website-events';
import {throttle, isEmpty} from 'lodash-es';

import moduleAddToCartMobile from '@/store/product-page/mobile/add-to-cart-module';

const pdpMobile = {
    strict: true,
    namespaced: true,
    state: {
        loadUrl: null, // for changing page data on groupId option change
        lastOrderUrl: null,
        pointsUrl: null,
        prozisPointsUpdateUrl: null,
        imagesUrl: null,
        isLoading: false,
        isLoadingCombos: false,
        preloadImage: {
            title: '',
            src: '',
            srcset: '',
            srcWebp: '',
            srcsetWebp: '',
            loadingImage: '',
        },
        criteoData: null,
        deviceDimensions: {
            screenWidth: 360,
            screenHeight: 667,
        },
        showRotateDevice: false,
        galleryData: {},
        basicInfoData: {},
        infoSectionData: {},
        addToCartData: null,
        comboData: null,
        campaignsData: null,
        tabsData: null,
        relatedProductsBlocks: null,
        isAppStore: false,
        isAppAndroid: false,
        isAppIOS: false,
        hammerVideoModal: {
            show: false,
            videoData: null
        }
    },
    mutations: {
        setLoadUrl(state, payload) {
            state.loadUrl = payload;
        },
        isLoading(state, payload) {
            state.isLoading = payload;
        },
        isLoadingCombos(state, payload) {
            state.isLoadingCombos = payload;
        },
        setPreloadImage(state, payload) {
            state.preloadImage = payload;
        },
        setLastOrderUrl(state, payload) {
            state.lastOrderUrl = payload;
        },
        setImagesUrl(state, payload) {
            state.imagesUrl = payload;
        },
        setProzisPointsUpdateUrl(state, payload) {
            state.prozisPointsUpdateUrl = payload;
        },
        setPointsUrl(state, payload) {
            state.pointsUrl = payload;
        },
        setCriteoData(state, payload) {
            state.criteoData = payload;
        },
        setDeviceDimensions(state) {
            const screenVw = window.screen.width;
            const screenVh = window.screen.height;
            if(screenVw > state.deviceDimensions.screenWidth) {
                state.deviceDimensions.screenWidth = screenVw;
            }
            if(screenVh > state.deviceDimensions.screenHeight) {
                state.deviceDimensions.screenHeight = screenVh;
            }
        },
        setShowRotateDevice(state, payload) {
            state.showRotateDevice = payload;
        },
        setGalleryData(state, payload) {
            state.galleryData = payload;
        },
        setBasicInfoData(state, payload) {
            state.basicInfoData = payload;
        },  
        setInfoSectionData(state, payload) {
            const addToCartData = state.addToCartData;
            const productInfo = addToCartData.productInfo;
            const kStrings = addToCartData.kStrings;
            state.infoSectionData = {
                name: productInfo.name,
                groupId: productInfo.groupId,
                ratingData: {
                    rating: productInfo.rating,
                    totalRatings: productInfo.ratingTotal,
                },
                points: productInfo.points,
                superPoints: productInfo.superPoints,
                prozisPointsFactor: productInfo.pointsFactor ?? 1,
                priceData: {
                    price: productInfo.minPrice,
                    priceCrossed: productInfo.pricePartner,
                    bestPriceCoupon: productInfo.bestPriceCoupon,
                },
                showLinkComments: productInfo.showLinkComments,
                stockMsg: addToCartData.hasStock ? kStrings.kIN_STOCK : kStrings.kPROD_STOCK_OUT,
                sharedData: !isEmpty(addToCartData.sharedData) ? addToCartData.sharedData : null,
                likesData: !isEmpty(addToCartData.likesData) ? addToCartData.likesData : null,
                fanLinkData: addToCartData.fanLink,
                frozenText: addToCartData.isFrozen ? kStrings.kPRODUTO_CONGELADO : null,
            };
        }, 
        updateInfoSectionPartial(state, payload) {
            state.infoSectionData = Object.assign({}, state.infoSectionData, payload);
        },
        setAddToCartData(state, payload) {
            state.addToCartData = payload;
        },
        setTabsData(state, payload) {
            state.tabsData = payload;
        },
        setComboData(state, payload) {
            state.comboData = payload;
        },
        setCampaignsData(state, payload) {
            state.campaignsData = payload;
        },
        setRelatedProductsBlocks(state, payload) {
            state.relatedProductsBlocks = payload;
        },
        setIsAppStore(state, payload) {
            state.isAppStore = payload;
        },
        setIsAppAndroid(state, payload) {
            state.isAppAndroid = payload;
        },
        setIsAppIOS(state, payload) {
            state.isAppIOS = payload;
        },
        updateHammerVideoModal(state, payload) {
            if(!payload) {
                payload = {
                    show: false,
                    videoData: null
                }
            }
            
            state.hammerVideoModal = Object.assign({}, state.hammerVideoModal, payload);
        },
    },

    actions: {
        // state, commit, dispatch, rootState, rootGetters
        initWsData(context, payload, dispatch, rootState, rootGetters) {
            //console.log('init Page PLAYLOAD: ', payload);
            try {
                context.dispatch('updateKstrings', payload.addToCart.kStrings, {root: true});
                context.commit('setLoadUrl', payload.loadUrl);
                context.dispatch('updateLastOrderUrl', payload);
                context.commit('setImagesUrl', payload.gallery.url);
                context.commit('setProzisPointsUpdateUrl', payload.prozisPointsUpdateUrl);
                context.commit('setPointsUrl', payload.pointsUrl);
                context.commit('setCriteoData', payload.criteoData);
                context.commit('setDeviceDimensions');
                context.dispatch('updateGalleryData', payload.gallery);
                context.dispatch('matchWith/setData', payload.addToCart.matchWith, {root: true});
                context.commit('setBasicInfoData', payload.basicInfo);
                context.commit('setAddToCartData', payload.addToCart);
                context.commit('setInfoSectionData', payload);
                context.commit('setCampaignsData', payload.campaigns);
                context.commit('setTabsData', payload.tabs);
                context.commit('setRelatedProductsBlocks', payload.relatedProducts);
                context.commit('setIsAppStore', payload.isAppStore);
                context.commit('setIsAppAndroid', payload.isAppAndroid);
                context.commit('setIsAppIOS', payload.isAppIOS);

                // base main store update
                const productInfo = payload.addToCart.productInfo;
                context.dispatch('priceCoupons/setData', payload.bestPriceCoupons ? payload.bestPriceCoupons : [], {root: true});
                context.dispatch('changeGroupId', {id: productInfo.groupId}, {root: true});
                context.dispatch('addToCartMobile/updateShowQuantityWidget', productInfo.showQuantity)
                if (payload.addToCart.hasOwnProperty('deliveryInfoUrl') &&
                    payload.addToCart.deliveryInfoUrl) {
                    context.dispatch('setDeliveryInfoUrl', payload.addToCart.deliveryInfoUrl, {root: true});
                }
                if (productInfo && payload.addToCart.hasOwnProperty('deliveryInfoSku') && payload.addToCart.deliveryInfoSku) {
                    context.dispatch('requestDeliveryInfo', {
                        groupId: productInfo.groupId,
                        varSku: payload.addToCart.deliveryInfoSku,
                        stock: payload.addToCart.hasStock,
                        groupStatus: productInfo.groupStatus
                    }, {root: true});
                }

                context.dispatch('updatePrice', {
                    price: productInfo.minPrice,
                    priceReference: productInfo.pricePartner,
                    bestPriceCoupon: productInfo.bestPriceCoupon,
                }, {root: true});
                context.dispatch('updateStockLabel', payload.basicInfo.stockMsg, {root: true});
                context.dispatch('updateHasStock', payload.basicInfo.hasStock, {root: true});

                // combos
                if (payload.addToCart.canCheckCombos) {
                    context.dispatch('updateComboData', productInfo.groupId);
                }
                
                // only works in good browsers wink wink
                //try{ screen.orientation.lock("portrait");} catch (e) {}
                
            } catch (e) {
                //console.log('page load failure!', e);
                if(typeof Bugsnag !== 'undefined'){
                    Bugsnag.notify('pdp page load failure failure!', 'pdp failed to set data', {}, 'error');
                }
            } finally {
                context.dispatch('setLoading', false);
                context.dispatch('initMovingElementsBehaviour');
            }
        },
        setLoading(context, payload) {
            context.commit('isLoading', payload);
        },
        setLoadingCombos(context, payload) {
            context.commit('isLoadingCombos', payload);
        },
        updateLastOrderUrl(context, payload) {
            if(payload.canShowLastOrderDate) {
                setTimeout(() => {
                    context.commit('setLastOrderUrl', payload.lastOrderUrl);
                }, 1000);
            }
        },
        updateCriteoData(context, payload) {
            context.commit('setCriteoData', payload);
        },
        updateShowRotateDevice(context, payload) {
            context.commit('setShowRotateDevice', payload);
        },
        async updateGalleryData(context, payload) {
            if(payload) {
                if(payload.preloadImage) {
                    context.commit('setPreloadImage', payload.preloadImage)
                }
                let url = payload.url ?? context.getters.imagesUrl;
                const {data} = await request.get(url, {sku: payload.defaultSku, id: payload.masterId});
                if(!isEmpty(data)) {
                    context.commit('setGalleryData', data);
                }
            } 
        },
        updateInfoSection(context, payload) {
            if(!isEmpty(payload)){
                context.commit('updateInfoSectionPartial', payload);
            }
        },
        updateBasicInfo(context, payload) {
            context.commit('setbasicInfoData', payload);
        },
        updateAddToCartData(context, payload) {
            context.commit('setAddToCartData', payload);
        },
        updateTabsData(context, payload) {
            context.commit('setTabsData', payload);
        },
        async updateComboData(context, payload) {
            context.commit('isLoadingCombos', true);
            const comboData = await apiCombos.getComboById(payload);
            if (comboData) {
                websiteEvents.eventCombo('show-block', 'PDP', null);
                context.commit('setComboData', comboData);
            } else { // ex: by changing groupId 
                context.commit('setComboData', null);
            }
            context.commit('isLoadingCombos', false);
        },
        updateCampaignsData(context, payload) {
            context.commit('setCampaignsData', payload);
        },
        updateRelatedProductsBlocks(context, payload) {
            context.commit('setRelatedProductsBlocks', payload);
        },
        initMovingElementsBehaviour(context, payload) { // navbar && float (cart) button behaviour
            let lastScrollTop =
                window.pageYOffset || document.documentElement.scrollTop;
            //let ticking = false;
            // moving elements
            let navBar = document.getElementById('nav-mob-bottom');
            let cartBuyWidget = document.getElementById('cartBuyWidget');
            // viewport sections
            //let pageWrapper = document.getElementById('mobileProductPage');
            let mainPdpSection = document.getElementById('mainPdpSection');
            let infoTabsSection = document.getElementById('infoTabsSection');
            let relatedProductsSection = document.getElementById(
                'relatedProductsSection');
            let isIOS = operatingSystem.isIOS();
            let throttleInterval = !isIOS ? 700 : 1100;

            window.addEventListener('scroll', throttle(() => {
                if (!context.getters.isAppStore && !navBar) {
                    return;
                }
                // detects new state and compares it with the new one
                // navbar
                const scrollTopPosition = window.pageYOffset || document.documentElement.scrollTop;
                let up = scrollTopPosition < lastScrollTop;
                //console.log('up?:::', up);
                let diamondModal = document.getElementById('diamond-modal');
                if (scrollTopPosition < 50) {
                    if (!diamondModal) { // my god
                        navBar && navBar.classList.add('nav-force-hide');
                    }
                } else if (!up) { // down direction
                    navBar && navBar.classList.remove('nav-force-hide');
                }

                if (!mainPdpSection) {
                    mainPdpSection = document.getElementById('mainPdpSection');
                }

                if (!relatedProductsSection) {
                    relatedProductsSection = document.getElementById(
                        'relatedProductsSection');
                }

                // float button
                if (!cartBuyWidget) {
                    cartBuyWidget = document.getElementById('cartBuyWidget');
                } else {
                    if (utilities.isElementOutViewport(mainPdpSection,
                        up ? throttleInterval : 0)) {
                        if (!utilities.isElementOutViewport(
                            relatedProductsSection)) { // related prods only removes the pen animations (still detached)
                            cartBuyWidget.classList.remove('open');
                        } else { // in between main section && related prodcuts
                            cartBuyWidget.classList.add('float-button');
                            setTimeout(() => {
                                cartBuyWidget.classList.add('open');
                            }, 250);

                        }
                    } else { // main area
                        cartBuyWidget.classList.remove('open');
                        setTimeout(() => {
                            cartBuyWidget.classList.remove('float-button');
                        }, 250);
                    }
                }
                // saves the new position for iteration.
                lastScrollTop = scrollTopPosition <= 0 ? 0 : scrollTopPosition;
            }, 300));
            
            
        },
    },
    getters: {
        loadUrl: state => state.loadUrl,
        lastOrderUrl: state => state.lastOrderUrl,
        pointsUrl: state => state.pointsUrl,
        prozisPointsUpdateUrl: state => state.prozisPointsUpdateUrl,
        imagesUrl: state => state.imagesUrl,
        isLoading: state => state.isLoading,
        isLoadingCombos: state => state.isLoadingCombos,
        preloadImage: state => state.preloadImage,
        criteoData: state => state.criteoData,
        deviceDimensions: state => state.deviceDimensions,
        showRotateDevice: state => state.showRotateDevice,
        galleryData: state => state.galleryData,
        basicInfoData: state => state.basicInfoData,
        infoSectionData: state => state.infoSectionData,
        addToCartData: state => state.addToCartData,
        tabsData: state => state.tabsData,
        comboData: state => state.comboData,
        campaignsData: state => state.campaignsData,
        relatedProductsBlocks: state => state.relatedProductsBlocks,
        isAppStore: state => state.isAppStore,
        isAppAndroid: state => state.isAppAndroid,
        isAppIOS: state => state.isAppIOS,
        hammerVideoModal: state => state.hammerVideoModal,
    },
    modules: {
        addToCartMobile: moduleAddToCartMobile,
    }
};

export default pdpMobile;